import { faImageSlash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as React from 'react';
import styled, { css } from 'styled-components';
import {
  ContentDetailsFragment,
  FilterContentsQuery,
} from '../../graphql/types';
import { useHasHpcMembers } from '../../hooks/useHasHpcMembers';
import useTracking from '../../hooks/useTracking';
import { GRAY100, ICON_SECONDARY } from '../../theme';
import { IMAGE_SIZE } from '../../utils/image';
import {
  EventCategories,
  EventLabels,
  EventNames,
  EventProfileCardNames,
  EventSubCategories,
} from '../../utils/trackingEvents';
import { Highlight } from '../Highlight';
import Button, { ButtonVariant } from '../reusable/Button';
import { ContentTypeIcon } from '../reusable/ContentTypeIcon';
import Text, { TextColor, TextKind, textStyleFrom } from '../reusable/Text';
import { useContentLibraryFilterContext } from './FilterContext';
import { getAudioVideoContent, getOnDemandContent } from './util';

const Item = styled.div`
  display: flex;
  gap: 16px;
`;

const PhotoBase = css`
  height: ${IMAGE_SIZE.THUMBNAIL / 2}px;
  width: ${IMAGE_SIZE.THUMBNAIL / 2}px;
  border-radius: 8px;
`;

const EmptyPhoto = styled.div`
  ${PhotoBase}
  background-color: white;
  border: 1px solid ${GRAY100};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Photo = styled.img`
  ${PhotoBase}
`;

const Line = styled.div`
  display: flex;
  gap: 8px;
`;

const Buttons = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;
`;

const StyledItemContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Chip = styled.div`
  border-radius: 999px;
  padding: 5px 8px;
  background-color: ${GRAY100};
`;

const GameplanMetadata = ({
  content: c,
}: {
  content: ContentDetailsFragment;
}) => {
  const content = getOnDemandContent(c);
  const hasHpMembers = useHasHpcMembers();

  // content genre is the lowest level genre
  // if there is no parent, then there is no subgenre
  // if there is a parent, then it is a subgenre, and we need the parent name
  const hasParentGenre = content?.contentGenre?.parent;
  const contentGenre = hasParentGenre
    ? content?.contentGenre?.parent?.name
    : content?.contentGenre?.name;
  const subGenre = hasParentGenre ? content?.contentGenre?.name : null;
  const genres = [contentGenre, subGenre].filter((v) => v);

  if (!hasHpMembers) {
    return (
      <Text kind={TextKind.LabelL} color={TextColor.Secondary}>
        {genres.join(' • ')}
      </Text>
    );
  }

  const primaryComponent = content?.gameplanMetadata?.primaryComponent?.name;
  const primarySubcomponents = (
    content?.gameplanMetadata?.primarySubcomponents ?? []
  )
    .map((node) => node?.name)
    .join(', ');

  if (!primaryComponent && !primarySubcomponents) {
    return null;
  }
  return (
    <Text kind={TextKind.LabelL} color={TextColor.Secondary}>
      {[primaryComponent, primarySubcomponents].filter(Boolean).join(' • ')}
    </Text>
  );
};

export const ContentLibraryList: React.FC<{
  contentsData: FilterContentsQuery;
  previewContent: (id: string) => void;
  selectItem: (
    id: string,
    subCategory?: EventSubCategories,
    cardName?: EventProfileCardNames,
  ) => void;
}> = ({ contentsData, previewContent, selectItem }) => {
  const { track } = useTracking();
  const { search } = useContentLibraryFilterContext();
  const handlePreview = (id: string) => {
    previewContent(id);
    track(EventNames.ButtonClick, {
      card_name: EventProfileCardNames.ContentLibraryModal,
      event_category: EventCategories.Content,
      content_id: id,
      event_label: EventLabels.Preview,
    });
  };
  const searchHighlightQuery = search.title?.trim().split(/[ ,]+/); // spaces and commas
  return (
    <div
      style={{
        display: 'flex',
        gap: 16,
        flexDirection: 'column',
        paddingBottom: 16,
      }}
    >
      {contentsData?.contents.edges.map(({ node }) => {
        const { title, heroPhoto, id, contentTime, contentLevel } = node;
        const content = getOnDemandContent(node);
        const { movementProgressions } = getAudioVideoContent(node) || {};

        return (
          <Item key={id} data-contentid={id}>
            {heroPhoto ? (
              <Photo
                src={`${heroPhoto.basePath}/${heroPhoto.id}/${IMAGE_SIZE.THUMBNAIL}x${IMAGE_SIZE.THUMBNAIL}.webp`}
                alt={title || 'Image'}
              />
            ) : (
              <EmptyPhoto>
                <FontAwesomeIcon
                  icon={faImageSlash}
                  color={ICON_SECONDARY}
                  fontSize={30}
                />
              </EmptyPhoto>
            )}
            <StyledItemContent>
              <Highlight
                style={textStyleFrom(TextKind.TitleXXs, TextColor.Primary)}
                query={searchHighlightQuery}
              >
                {title}
              </Highlight>
              <Line>{content && <GameplanMetadata content={content} />}</Line>
              <Line style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {node.__typename && (
                  <ContentTypeIcon typename={node.__typename} />
                )}
                {(contentTime?.name || contentLevel?.name) && (
                  <Text kind={TextKind.LabelL} color={TextColor.Secondary}>
                    {[contentTime?.name, contentLevel?.name]
                      .filter(Boolean)
                      .join(' • ')}
                  </Text>
                )}
              </Line>

              <Line
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                {movementProgressions?.edges.map(({ node }) => (
                  <Chip key={node.id}>
                    <Highlight
                      style={textStyleFrom(
                        TextKind.LabelL,
                        TextColor.Secondary,
                      )}
                      query={searchHighlightQuery}
                    >
                      {node.description}
                    </Highlight>
                  </Chip>
                ))}
              </Line>

              <Buttons>
                <Button onClick={() => handlePreview(id)}>Preview</Button>
                <Button
                  onClick={() => selectItem(id)}
                  variant={ButtonVariant.Dark}
                >
                  Select
                </Button>
              </Buttons>
            </StyledItemContent>
          </Item>
        );
      })}
    </div>
  );
};
